import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { RequestOptions } from './api';
import { ApiError } from './api';
import { useApi } from './auth';

// Accès aux données : la clé de cache est le chemin et ses paramètres ; une écriture
// invalide les lectures dont le chemin commence par les préfixes indiqués.

type Query = NonNullable<RequestOptions['query']>;

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
        refetchOnWindowFocus: false,
      },
    },
  });
}

export function useApiQuery<T>(
  path: string | null,
  query?: Query,
  options: Omit<UseQueryOptions<T, Error>, 'queryKey' | 'queryFn'> = {},
) {
  const api = useApi();
  return useQuery<T, Error>({
    queryKey: [path, query ?? {}],
    queryFn: () => api.get<T>(path as string, query),
    enabled: path !== null && (options.enabled ?? true),
    ...options,
  });
}

export function useApiMutation<TBody, TResult = unknown>(
  method: string,
  path: string | ((body: TBody) => string),
  options: { invalidate?: string[]; onSuccess?: (result: TResult, body: TBody) => void } = {},
) {
  const api = useApi();
  const client = useQueryClient();
  return useMutation<TResult, Error, TBody>({
    mutationFn: (body) => {
      const target = typeof path === 'function' ? path(body) : path;
      return api.request<TResult>(
        method,
        target,
        body instanceof FormData ? { form: body } : body === undefined ? {} : { body },
      );
    },
    onSuccess: async (result, body) => {
      await Promise.all(
        (options.invalidate ?? []).map((prefix) =>
          client.invalidateQueries({
            predicate: (q) => typeof q.queryKey[0] === 'string' && q.queryKey[0].startsWith(prefix),
          }),
        ),
      );
      options.onSuccess?.(result, body);
    },
  });
}
